import { useCallback, useEffect, useRef, useState } from "react";
import createTunnus from "@yleisradio/tunnus-sdk";

import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { setWindowProperties } from "../../service/window";
import { LoginData } from "../../types/common";

const defaultLoginData = {
  loggedIn: false,
  residenceInFinland: false,
  userId: undefined
};

export const tunnusExists = () => window.ylePlayer.tunnus !== undefined;

export const useLoginData = ({
  requireLogin
}: {
  requireLogin: boolean;
}): LoginData | undefined => {
  const loginInitialized = useRef(false);

  const { language, theme } = useEmbedParametersContext();

  const [initialLoginData, setInitialLoginData] = useState<LoginData>();

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const loadLoginData = useCallback(async () => {
    if (!requireLogin) {
      // If login is not required, use default (not logged in) data
      setInitialLoginData({ ...defaultLoginData });
    } else {
      if (!tunnusExists()) {
        const tunnus = createTunnus({
          environment: __YLE_TUNNUS_SDK_ENV,
          initiatingApp: "player-static",
          language: language === "swe" ? "sv" : "fi",
          returnToUrl: window.location.href,
          theme: theme === "light" ? "light" : "dark",
          trackingAppName: `player_static_${__YLE_TUNNUS_SDK_ENV === "production" ? "prod" : "test"}`
        });

        setWindowProperties({ tunnus });
      }

      window.ylePlayer.tunnus
        .getUserInfo()
        .then((userInfo: UserInfo) => {
          if (userInfo) {
            setInitialLoginData({
              loggedIn: true,
              residenceInFinland: !!userInfo?.residenceInFinland,
              userId: userInfo.userId
            });
          } else {
            setInitialLoginData(defaultLoginData);
          }
        })
        .catch(() => {
          setInitialLoginData(defaultLoginData);
        });
    }
  }, [language, requireLogin, theme]);

  useEffect(() => {
    if (!loginInitialized.current) {
      void loadLoginData();
      loginInitialized.current = true;
    }
  }, [loadLoginData]);

  return initialLoginData;
};
